import AnalyticsFilterInputs from "../../types/analytics/AnalyticsFilterInputs";
import NudgeDto, { UsageNudgeAudience } from "../../types/nudges/NudgeDto";

export interface UsageNudgePreviewDto {
  canSend: boolean;
  cooldownEndsAt: string | null;
  recipientEmployeeId: number;
  recipientName: string;
  audience: UsageNudgeAudience;
  outstandingCount: number;
  previewLines: string[];
  remainingPreviewCount: number;
  deliveryChannel: NudgeDto["deliveryChannel"];
}

export interface UsageNudgeSendResultDto {
  nudge: NudgeDto;
  deliveryFailed: boolean;
}

export interface UsageNudgeBatchPreviewSampleDto {
  recipientEmployeeId: number;
  recipientName: string;
  outstandingCount: number;
  canSend: boolean;
  cooldownEndsAt: string | null;
}

export interface UsageNudgeBatchPreviewSummaryDto {
  selectedCount: number;
  eligibleCount: number;
  cooldownCount: number;
  notNudgeableCount: number;
  sample: UsageNudgeBatchPreviewSampleDto[];
}

export interface UsageNudgeBatchPreviewDto {
  summary: UsageNudgeBatchPreviewSummaryDto;
  /** Legacy full list; empty for bulk preview. */
  recipients?: UsageNudgePreviewDto[];
}

export interface UsageNudgeBatchEnqueueResultDto {
  batchId: number;
  recipientCount: number;
}

export type UsageNudgeBatchSendStatus =
  | "READY"
  | "IN-PROGRESS"
  | "SENT"
  | "FAILED";

export type UsageNudgeBatchSendResult =
  | "FULL-SUCCESS"
  | "PARTIAL-SUCCESS"
  | "FAILED";

export type UsageNudgeBatchRecipientOutcome =
  | "PENDING"
  | "SENT"
  | "COOLDOWNACTIVE"
  | "COOLDOWN-ACTIVE"
  | "NOTNUDGEABLE"
  | "NOT-NUDGEABLE"
  | "DELIVERYFAILED"
  | "DELIVERY-FAILED"
  | "FAILED";

export interface UsageNudgeBatchRecipientStatusDto {
  recipientEmployeeId: number;
  recipientName: string;
  outcome: UsageNudgeBatchRecipientOutcome;
  dateSent: string | null;
  lastErrorCode: string | null;
  lastErrorDetail: string | null;
}

export interface UsageNudgeBatchStatusDto {
  batchId: number;
  senderEmployeeId: number;
  senderName: string;
  dateCreated: string;
  dateStarted: string | null;
  dateCompleted: string | null;
  sendStatus: UsageNudgeBatchSendStatus;
  sendResult: UsageNudgeBatchSendResult | null;
  sendResultSummary: string | null;
  recipientCount: number;
  sentCount: number;
  skippedCount: number;
  failedCount: number;
  pendingCount: number;
  recipients: UsageNudgeBatchRecipientStatusDto[];
}

export type UsageNudgePreviewResponse =
  | { status: "OK"; preview: UsageNudgePreviewDto }
  | { status: "NOT_NUDGEABLE" }
  | { status: "FORBIDDEN" }
  | { status: "ERROR" };

export type UsageNudgeSendResponse =
  | { status: "OK"; result: UsageNudgeSendResultDto }
  | {
      status: "COOLDOWN_ACTIVE";
      cooldownEndsAt: string;
      lastSentAt: string;
      senderName: string | null;
    }
  | { status: "NOT_NUDGEABLE" }
  | { status: "FORBIDDEN" }
  | { status: "DELIVERY_FAILED"; nudge: NudgeDto }
  | { status: "ERROR" };

export type UsageNudgeBatchPreviewResponse =
  | { status: "OK"; preview: UsageNudgeBatchPreviewDto }
  | { status: "FORBIDDEN" }
  | { status: "BATCH_LIMIT_EXCEEDED"; maxRecipients: number }
  | { status: "ERROR"; error?: string };

export type UsageNudgeBatchEnqueueResponse =
  | { status: "QUEUED"; result: UsageNudgeBatchEnqueueResultDto }
  | { status: "FORBIDDEN" }
  | { status: "BATCH_LIMIT_EXCEEDED"; maxRecipients: number }
  | { status: "ERROR"; error?: string };

export type UsageNudgeBatchStatusResponse =
  | { status: "OK"; batch: UsageNudgeBatchStatusDto }
  | { status: "FORBIDDEN" }
  | { status: "NOT_FOUND" }
  | { status: "ERROR" };

const PREVIEW_BASE = "analytics/usage/nudge/preview";
const SEND_BASE = "analytics/usage/nudge";
const BATCH_PREVIEW_BASE = "analytics/usage/nudge/batch/preview";
const BATCH_SEND_BASE = "analytics/usage/nudge/batch";

async function parseErrorBody(response: Response): Promise<string | undefined> {
  try {
    const body = (await response.json()) as { error?: string };
    return body?.error;
  } catch {
    return undefined;
  }
}

class usageNudgeApi {
  constructor(private accessToken: string | undefined) {}

  async preview(
    filters: AnalyticsFilterInputs,
    recipientEmployeeId: number,
    audience: UsageNudgeAudience,
  ): Promise<UsageNudgePreviewResponse> {
    try {
      const response = await window.fetch(
        `${process.env.REACT_APP_API_URL}/${PREVIEW_BASE}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${this.accessToken}`,
          },
          body: JSON.stringify({
            filters,
            recipientEmployeeId,
            audience,
          }),
        },
      );

      if (response.status === 403) return { status: "FORBIDDEN" };
      if (response.status === 400) return { status: "NOT_NUDGEABLE" };
      if (!response.ok) return { status: "ERROR" };

      const preview = (await response.json()) as UsageNudgePreviewDto;
      return preview ? { status: "OK", preview } : { status: "ERROR" };
    } catch {
      return { status: "ERROR" };
    }
  }

  async send(
    filters: AnalyticsFilterInputs,
    recipientEmployeeId: number,
    audience: UsageNudgeAudience,
    adminMessage?: string,
  ): Promise<UsageNudgeSendResponse> {
    try {
      const response = await window.fetch(
        `${process.env.REACT_APP_API_URL}/${SEND_BASE}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${this.accessToken}`,
          },
          body: JSON.stringify({
            filters,
            recipientEmployeeId,
            audience,
            adminMessage: adminMessage?.trim() || null,
          }),
        },
      );

      if (response.status === 403) return { status: "FORBIDDEN" };
      if (response.status === 400) return { status: "NOT_NUDGEABLE" };
      if (response.status === 409) {
        const details = (await response.json()) as {
          cooldownEndsAt?: string;
          lastSentAt?: string;
          senderName?: string | null;
        };
        return {
          status: "COOLDOWN_ACTIVE",
          cooldownEndsAt: details.cooldownEndsAt ?? new Date().toISOString(),
          lastSentAt: details.lastSentAt ?? new Date().toISOString(),
          senderName: details.senderName ?? null,
        };
      }
      if (response.status === 503) {
        const details = (await response.json()) as { nudge?: NudgeDto };
        if (details.nudge) {
          return { status: "DELIVERY_FAILED", nudge: details.nudge };
        }
        return { status: "ERROR" };
      }
      if (!response.ok) return { status: "ERROR" };

      const result = (await response.json()) as UsageNudgeSendResultDto;
      return result?.nudge
        ? { status: "OK", result }
        : { status: "ERROR" };
    } catch {
      return { status: "ERROR" };
    }
  }

  async previewBatch(
    filters: AnalyticsFilterInputs,
    recipientEmployeeIds: number[],
    audience: UsageNudgeAudience,
  ): Promise<UsageNudgeBatchPreviewResponse> {
    try {
      const response = await window.fetch(
        `${process.env.REACT_APP_API_URL}/${BATCH_PREVIEW_BASE}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${this.accessToken}`,
          },
          body: JSON.stringify({ filters, recipientEmployeeIds, audience }),
        },
      );

      if (response.status === 403) return { status: "FORBIDDEN" };
      if (response.status === 400) {
        const body = (await response.json()) as {
          error?: string;
          maxRecipients?: number;
        };
        if (body.error === "BATCH_LIMIT_EXCEEDED") {
          return {
            status: "BATCH_LIMIT_EXCEEDED",
            maxRecipients: body.maxRecipients ?? 5000,
          };
        }
        return { status: "ERROR", error: body.error };
      }
      if (!response.ok) {
        return { status: "ERROR", error: await parseErrorBody(response) };
      }

      const preview = (await response.json()) as UsageNudgeBatchPreviewDto;
      return preview?.summary
        ? { status: "OK", preview }
        : { status: "ERROR" };
    } catch {
      return { status: "ERROR" };
    }
  }

  async enqueueBatch(
    filters: AnalyticsFilterInputs,
    recipientEmployeeIds: number[],
    audience: UsageNudgeAudience,
    adminMessage?: string,
  ): Promise<UsageNudgeBatchEnqueueResponse> {
    try {
      const response = await window.fetch(
        `${process.env.REACT_APP_API_URL}/${BATCH_SEND_BASE}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${this.accessToken}`,
          },
          body: JSON.stringify({
            filters,
            recipientEmployeeIds,
            audience,
            adminMessage: adminMessage?.trim() || null,
          }),
        },
      );

      if (response.status === 403) return { status: "FORBIDDEN" };
      if (response.status === 400) {
        const body = (await response.json()) as {
          error?: string;
          maxRecipients?: number;
        };
        if (body.error === "BATCH_LIMIT_EXCEEDED") {
          return {
            status: "BATCH_LIMIT_EXCEEDED",
            maxRecipients: body.maxRecipients ?? 5000,
          };
        }
        return { status: "ERROR", error: body.error };
      }
      if (response.status !== 202) {
        return { status: "ERROR", error: await parseErrorBody(response) };
      }

      const result = (await response.json()) as UsageNudgeBatchEnqueueResultDto;
      return result?.batchId
        ? { status: "QUEUED", result }
        : { status: "ERROR" };
    } catch {
      return { status: "ERROR" };
    }
  }

  async getBatchStatus(batchId: number): Promise<UsageNudgeBatchStatusResponse> {
    try {
      const response = await window.fetch(
        `${process.env.REACT_APP_API_URL}/${BATCH_SEND_BASE}/${batchId}/status`,
        {
          headers: {
            Authorization: `Bearer ${this.accessToken}`,
          },
        },
      );

      if (response.status === 403) return { status: "FORBIDDEN" };
      if (response.status === 404) return { status: "NOT_FOUND" };
      if (!response.ok) return { status: "ERROR" };

      const batch = (await response.json()) as UsageNudgeBatchStatusDto;
      return batch?.batchId ? { status: "OK", batch } : { status: "ERROR" };
    } catch {
      return { status: "ERROR" };
    }
  }
}

export default usageNudgeApi;
